<template>
  <div class="styled-basket">
    <alert-viewer v-show="!hideAlertViewer"
                  namespace="basket"></alert-viewer>

    <template v-if="!refresh || !loading">
      <base-basket
        v-slot="{
          orderLines,
          voucherCodes,
          totals,
          refreshData,
          expectingNewLine,
          initialLoading,
          messages,
          clearMessages,
        }"
        :show-carriage="showCarriage"
        @success="refreshData">
        <template v-if="!initialLoading">
          <b-alert variant="danger"
                   :show="invalidVouchers && Object.keys( invalidVouchers ).length > 0">
            <h2 class="alert-heading">
              Error:
            </h2>
            <p class="alert-msg">
              The following voucher codes have expired or are not valid for this order:
            </p>
            <ul class="alert-list">
              <li v-for="(invalidVoucher, index) in invalidVouchers"
                  :key="index"
                  class="alert-item">
                {{ invalidVoucher }}
              </li>
            </ul>
          </b-alert>

          <b-alert :show="messages && messages.length > 0"
                   dismissible
                   @dismissed="clearMessages">
            <h2 class="alert-heading">
              Note:
            </h2>
            <ul class="alert-list">
              <li v-for="(message, index) in messages"
                  :key="index"
                  class="alert-item">
                {{ message }}
              </li>
            </ul>
          </b-alert>
          <b-alert :show="hasWarnings">
            <h2 class="alert-heading">
              Warning:
            </h2>
            <ul class="alert-list">
              <!-- eslint-disable vue/no-v-html -->
              <li v-for="(warning, index) in warnings"
                  :key="index"
                  class="alert-item"
                  v-html="warning">
              </li>
              <!-- eslint-enable vue/no-v-html -->
            </ul>
          </b-alert>
          <template v-if="orderLines && orderLines.length > 0">
            <simple-basket-order-lines-list :no-delete="readonly">
              <simple-basket-order-line
                v-for="(orderLine, index) in orderLines"
                :key="orderLine.id"
                v-model="orderLines[index]"
                :lock-quantity="readonly"
                :no-delete="readonly"
                :input-class="inputClass"
                :currency="currency"
                :locale="locale"
                btn-class="btn btn-danger"
                @update="refreshData()">
                <template #default>
                  <div>
                    <component
                      :is="orderLine.hasWebPage ? 'a' : 'span'"
                      :href="`/products/${orderLine.productId}`"
                      target="_blank"
                      class="product-display-name">
                      {{ orderLine.productDisplayName }}
                    </component>
                  </div>
                </template>
                <template #delete-label>
                  <font-awesome-icon
                    :icon="['far', 'trash-alt']"
                    aria-hidden="true"></font-awesome-icon>
                </template>
                <template #loading>
                  <loading-spinner></loading-spinner>
                </template>
              </simple-basket-order-line>

              <tr v-if="expectingNewLine"
                  class="simple-basket__loading">
                <td colspan="100%">
                  <loading-spinner></loading-spinner>
                </td>
              </tr>
            </simple-basket-order-lines-list>

            <div class="simple-basket__summary">
              <div class="simple-basket__pricing">
                <div v-if="voucherError"
                     class="alert alert-danger simple-basket__error">
                  {{ voucherError }}
                </div>

                <order-totals :value="totals"
                              :currency="currency"
                              :locale="locale"></order-totals>

                <apply-voucher
                  v-if="enableVouchers"
                  :applied-voucher-codes="voucherCodes"
                  :input-class="inputClass"
                  :btn-class="btnClass"
                  can-remove-voucher
                  @update="refreshData()"
                  @response="onApplyVoucherResponse">
                  <template #default>
                    Apply
                  </template>
                  <template #remove-voucher-icon>
                    <font-awesome-icon :icon="['fas', 'trash-alt']"
                                       aria-hidden="true"></font-awesome-icon>
                  </template>
                </apply-voucher>
              </div>

              <div v-if="enableClear"
                   class="simple-basket__clear">
                <delete-all-order-lines
                  class="btn btn-danger"
                  @update="refreshData()"></delete-all-order-lines>
              </div>

              <slot name="submit"></slot>
            </div>
          </template>

          <template v-else>
            <div class="simple-basket__empty">
              <slot name="empty">
                Your basket is currently empty.
              </slot>
            </div>
          </template>
        </template>
        <template v-else>
          <loading-spinner></loading-spinner>
        </template>
      </base-basket>
    </template>
    <template v-else>
      <slot name="loading">
        <loading-spinner></loading-spinner>
      </slot>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import Ecommerce, { BasketStepResource } from "spiritweb.packages.ecommerce";
import {
  AlertViewer,
  NotificationModule,
} from "spiritweb.packages.web-account";
import { getBasketProducts } from "~/Scripts/resources/basketStepResource";
import LoadingSpinner from "~/Components/Base/LoadingSpinner.vue";

Vue.use( Ecommerce );

export default {
  name: "StyledBasket",
  components: {
    LoadingSpinner,
    AlertViewer,
  },
  props: {
    enableVouchers: {
      type: Boolean,
      default: false,
    },
    enableClear: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: String,
      default: "form-control",
    },
    btnClass: {
      type: String,
      default: null,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
    loggedIn: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "GBP",
    },
    locale: {
      type: String,
      default: "en-GB",
    },
    showCarriage: {
      type: Boolean,
      default: false,
    },
    hideAlertViewer: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      bookings: [],
      showBookingModal: false,
      selectedOrderLine: {},
      selectedInstance: null,
      selectedPricingSchedule: null,
      showDeliveryAddressModal: null,
      products: [],
      warnings: [],
      internalLoggedIn: false,
      voucherError: null,
      loading: false,
      invalidVouchers: null,
    };
  },
  computed: {
    selectedOrderLines () {
      let result = null;

      if ( this.selectedOrderLine?.id ) {
        result = [this.selectedOrderLine.id];
      }

      return result;
    },

    hasWarnings () {
      return this.warnings?.length > 0;
    },
  },
  watch: {
    refresh ( val ) {
      if ( val ) {
        this.refreshData();
      }
    },
  },
  methods: {
    refreshData () {
      this.warnings = [];

      getBasketProducts()
        .then( response => {
          this.products = response.data.products;
          this.warnings = response.data.warnings;

          return BasketStepResource.validateOrder()
            .then( ( res ) => {
              console.log( "res", res );
            } )
            .catch( ( err ) => {
              const alert = new NotificationModule.SwAlert( err.message, {
                namespace: "basket",
                variant: "danger",
              } );
              this.$notifications.add( alert );
            } );
        } )
        .then( response => {
          if ( response?.data ) {
            this.invalidVouchers = response.data.invalidVouchers;
          }
        } )
        .finally( () => {
          this.$emit( "refreshed" );
        } );
    },
    refreshAddresses () {
      this.$emit( "refresh-addresses" );
    },
    onApplyVoucherResponse ( response ) {
      this.voucherError = null;

      if ( response ) {
        if ( !response.valid ) {
          this.voucherError = response.userMessage;
        }
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.styled-basket {

  /deep/ .order-line-list__heading {
    font-weight: 600;
  }

  /deep/ .order-lines-list__table {
    width: 100%;
  }

  /deep/ .delete-order-line {
    background-color: transparent;
    border: none;
    color: inherit;
    display: flex;
    justify-content: center;
    align-items: center;

    .loading-container {
      margin: 0;

      & > svg {
        width: 16px;
        height: 16px;
      }
    }

    &:active {
      color: inherit;
      background-color: transparent;
      box-shadow: 0 0 0 0.2rem lighten(@color-primary, 20%) !important;
    }

    svg {
      margin: 0;
    }
  }

  /deep/ .order-line__quantity {
    width: 90px;
  }

  /deep/ .order-line__price {
    text-align: center;
    width: 140px;
  }

  /deep/ .product-display-name {
    font-size: 14px;
    font-weight: 700;
  }

  /deep/ .order-line__remove {
    width: 30px;

    & > button {
      width: unset;
    }
  }

  /deep/ .order-line {
    border-top: 1px solid lightgrey;
    border-bottom: 1px solid lightgrey;
  }

  /deep/ .order-line__name {
    width: auto;
  }

  /deep/ .order-line__data {
    padding: 10px 0;
  }

  /deep/ .order-totals {
    margin-top: 1rem;
    display: flex;
    width: 350px;
    margin-left: auto;

    @media ( max-width: 767px ) {
      width: 100%;
    }
  }

  /deep/ .order-totals__content,
  /deep/ .order-totals__table {
    width: 100%;
  }

  /deep/ .order-totals__table {
    th {
      font-weight: 700;
    }

    td {
      font-weight: 700;
      padding-left: 4rem;
      text-align: right;
    }
  }

  /deep/ .apply-voucher {
    display: flex;
    flex-direction: column;
    width: 350px;
    margin-top: 1rem;
    margin-left: auto;

    @media ( max-width: 767px ) {
      width: 100%;
    }

    &__current-voucher {
      margin-bottom: 1rem;
    }

    &__input-container {
      display: flex;
      align-items: center;
    }

    &__input {
      width: 100% !important;
      margin-right: 0;
    }

    &__button {
      margin-left: 0.5rem;
      min-width: 107px;
    }

    &__applied-heading {
      display: block;
      margin-bottom: 0.5rem;
    }

    &__remove-code {
      display: inline-block;
      background-color: @color-secondary;
      color: #fff;
      border-radius: 5px;
      font-size: 0.75rem;
      padding: 3px 10px;
      transition: box-shadow .2s ease-out,
      background-color .2s ease-out;
      user-select: none;
      margin-left: 0.5rem;

      &:active {
        box-shadow: 0 0 0 0.2rem lighten(@color-secondary, 20%);
        background-color: darken(@color-secondary, 20%);
      }

      @media ( max-width: 767px ) {
        padding: 8px 15px;
        font-size: 1rem;
      }

    }

    ul {
      display: flex;
      flex-direction: column;
      list-style: none;
      padding: 0;
      gap: 0.5rem;
      margin: 0 0 1rem;
    }

    &__item {
      display: flex;
      align-items: center;
      font-weight: 600;
      justify-content: space-between;
    }
  }

  .loading-container {
    margin: 40px 0;
  }
}
</style>
